import { Component } from '@theme/component';
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';

/**
 * Swipeable review carousel with chevron controls.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} track
 * @property {HTMLElement} controls
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 *
 * @extends {Component<Refs>}
 */
class ReviewCarouselComponent extends Component {
  /** @type {number} */
  #currentIndex = 0;

  /** @type {MediaQueryList | undefined} */
  #mediaQuery;

  connectedCallback() {
    super.connectedCallback();

    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;

    track.addEventListener('scroll', this.#handleScroll, { passive: true });

    this.#mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    this.#mediaQuery.addEventListener('change', this.#handleResize);
    window.addEventListener('resize', this.#handleResize, { passive: true });

    requestAnimationFrame(() => {
      this.#syncSlideWidths();
      this.#normalizeScrollPosition();
      this.#syncIndex();
      this.#updateControls();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.track?.removeEventListener('scroll', this.#handleScroll);
    this.#mediaQuery?.removeEventListener('change', this.#handleResize);
    window.removeEventListener('resize', this.#handleResize);
  }

  updatedCallback() {
    super.updatedCallback();
    requestAnimationFrame(() => {
      this.#syncSlideWidths();
      this.#normalizeScrollPosition();
      this.#syncIndex();
      this.#updateControls();
    });
  }

  /** @returns {boolean} */
  #isDesktopCarousel() {
    return !window.matchMedia(MOBILE_MEDIA_QUERY).matches && this.dataset.desktopCarousel === 'true';
  }

  /** @returns {boolean} */
  #isCarouselMode() {
    if (window.matchMedia(MOBILE_MEDIA_QUERY).matches) return true;

    return this.dataset.desktopCarousel === 'true';
  }

  #syncSlideWidths() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return;

    if (!this.#isCarouselMode()) {
      track.style.removeProperty('--rc-slide-width');
      return;
    }

    if (this.#isDesktopCarousel()) {
      track.style.removeProperty('--rc-slide-width');
      return;
    }

    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    const slideWidth = Math.max(0, track.clientWidth - 2.5 * rootFontSize);
    track.style.setProperty('--rc-slide-width', `${slideWidth}px`);
  }

  #normalizeScrollPosition() {
    const track = this.refs.track;
    const slides = this.slides;
    if (!(track instanceof HTMLElement) || slides.length === 0) return;
    if (!this.#isCarouselMode()) return;

    const activeSlide = slides[this.#currentIndex] ?? slides[0];
    if (!activeSlide) return;

    const targetScrollLeft = this.#getTargetScrollLeft(track, activeSlide);
    if (Math.abs(track.scrollLeft - targetScrollLeft) > 1) {
      track.scrollLeft = targetScrollLeft;
    }
  }

  /** @returns {HTMLElement[]} */
  get slides() {
    const track = this.refs.track;
    if (!(track instanceof HTMLElement)) return [];

    return /** @type {HTMLElement[]} */ ([...track.querySelectorAll('.review-carousel__item')]);
  }

  /**
   * @param {Event} [event]
   */
  previous(event) {
    event?.preventDefault();
    this.#scrollToSlide(this.#currentIndex - 1);
  }

  /**
   * @param {Event} [event]
   */
  next(event) {
    event?.preventDefault();
    this.#scrollToSlide(this.#currentIndex + 1);
  }

  /**
   * @param {HTMLElement} track
   * @returns {number}
   */
  #getSnapInset(track) {
    const style = getComputedStyle(track);
    return parseFloat(style.scrollPaddingLeft) || 0;
  }

  /**
   * @param {HTMLElement} track
   * @param {HTMLElement} slide
   * @returns {number}
   */
  #getTargetScrollLeft(track, slide) {
    const snapInset = this.#getSnapInset(track);
    const slideRect = slide.getBoundingClientRect();
    const trackRect = track.getBoundingClientRect();

    return track.scrollLeft + slideRect.left - trackRect.left - snapInset;
  }

  /** @param {number} index */
  #scrollToSlide(index) {
    const track = this.refs.track;
    const slides = this.slides;
    if (!(track instanceof HTMLElement) || slides.length === 0) return;

    const targetIndex = Math.max(0, Math.min(index, slides.length - 1));
    const slide = slides[targetIndex];
    if (!slide) return;

    track.scrollTo({
      left: this.#getTargetScrollLeft(track, slide),
      behavior: 'smooth',
    });

    this.#currentIndex = targetIndex;
    this.#updateControls();
  }

  #handleResize = () => {
    this.#syncSlideWidths();
    this.#normalizeScrollPosition();
    this.#syncIndex();
    this.#updateControls();
  };

  #handleScroll = () => {
    this.#syncIndex();
    this.#updateControls();
  };

  #syncIndex() {
    const track = this.refs.track;
    const slides = this.slides;
    if (!(track instanceof HTMLElement) || slides.length === 0) {
      this.#currentIndex = 0;
      return;
    }

    const snapInset = this.#getSnapInset(track);
    const trackLeft = track.getBoundingClientRect().left + snapInset;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    for (const [index, slide] of slides.entries()) {
      const distance = Math.abs(slide.getBoundingClientRect().left - trackLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    }

    this.#currentIndex = closestIndex;
  }

  #updateControls() {
    const track = this.refs.track;
    const controls = this.refs.controls;
    const previous = this.refs.previous;
    const next = this.refs.next;

    if (!(track instanceof HTMLElement) || !(controls instanceof HTMLElement)) return;

    const slides = this.slides;

    if (!this.#isCarouselMode() || slides.length <= 1) {
      controls.hidden = true;
      return;
    }

    controls.hidden = false;

    if (previous instanceof HTMLButtonElement) {
      previous.disabled = this.#currentIndex <= 0;
    }

    if (next instanceof HTMLButtonElement) {
      next.disabled = this.#currentIndex >= slides.length - 1;
    }
  }
}

if (!customElements.get('review-carousel-component')) {
  customElements.define('review-carousel-component', ReviewCarouselComponent);
}
